import { DevTool } from "@hookform/devtools";

import { LinkWithIcon, SearchBar, SwitchAppButton } from "../../../components";
import { countries } from "../../../constants";
import { SaleLineItems } from "../SaleLineItems";
import { IGatewayFormViewProps } from "./GatewayForm";
import { GatewaySkeletonLoader } from "./GatewaySkeletonLoader";
import { switchappPaymentChannel } from "../../../enums";
import { HookFormSelect, SubmitButton, TextInput } from "../../../hook-form";

import blueCheckMarkIcon from "../../../assets/icons/tick-circle-blue.svg";
import bankTransferIcon from "../../../assets/icons/bank-transfer-icon.png";
import closeIcon from "../../../assets/icons/close-circle.svg";
import { BankTransferOptions } from "./BankTransferOptions";
import { VirtualBankTransferConfirmationPending } from "./VirtualBankTransferConfirmationPending";

export const View = ({
  searchLineItemsText,
  isActive,

  isLoadingMerchantGateways,
  isSuccessMerchantGateways,
  merchantEnabledGateways,
  searchGatewaysText,
  setSearchGatewaysText,

  isLineItemQtyInputTempDisabled,
  setValue,
  saleTotal,

  hostedPaymentLink,

  isVirtualAccountPayment,
  isVirtualBankTransferConfirmationPending,
  gatewayReservedVirtualAccounts,
  selectedGatewayCode,
  selectedPaymentChannel,
  setGatewayCode,
  setSelectedPaymentChannel,
  closeVirtualBankAccountsView,
  setBankTransferCompleted,
  showSuccessPage,

  formValues,
  control,
  errors,
  handleSubmit,
  register,
}: IGatewayFormViewProps) => {
  return (
    <div className="gateway-form--container">
      <form onSubmit={handleSubmit}>
        {isActive ? (
          <SaleLineItems
            saleTotal={saleTotal}
            isLineItemQtyInputTempDisabled={isLineItemQtyInputTempDisabled}
            hostedPaymentLink={hostedPaymentLink}
            setValue={setValue}
            register={register}
            errors={errors}
          />
        ) : null}

        {isVirtualBankTransferConfirmationPending ? (
          <VirtualBankTransferConfirmationPending
            closeVirtualBankAccountsView={closeVirtualBankAccountsView}
            hostedPaymentLink={hostedPaymentLink}
            showSuccessPage={showSuccessPage}
          />
        ) : isVirtualAccountPayment ? (
          <BankTransferOptions
            closeVirtualBankAccountsView={closeVirtualBankAccountsView}
            gatewayReservedVirtualAccounts={gatewayReservedVirtualAccounts}
            setBankTransferCompleted={setBankTransferCompleted}
          />
        ) : (
          <>
            {hostedPaymentLink.is_temp_payment_link ||
            hostedPaymentLink.is_top_up_payment_link ? null : (
              <TextInput
                name="customer.email"
                label="Email"
                type="text"
                register={register}
                errors={errors}
              />
            )}

            {!hostedPaymentLink.require_customer_full_name ? null : (
              <TextInput
                name="customer.full_name"
                label="Full Name"
                type="text"
                register={register}
                errors={errors}
              />
            )}

            {!hostedPaymentLink.require_customer_address ? null : (
              <TextInput
                name="customer.address"
                label="Address"
                type="text"
                register={register}
                errors={errors}
              />
            )}

            {!hostedPaymentLink.require_customer_phone_number ? null : (
              <TextInput
                name="customer.phone_number"
                label="Phone Number"
                type="text"
                register={register}
                errors={errors}
              />
            )}

            <HookFormSelect
              name="country"
              label="Country Or Region"
              infoIconText="Your country or region"
              selectItems={[]}
              selectGroupedItems={countries}
              register={register}
              errors={errors}
            />

            <div className="flex-container justify-content--center mt-5">
              <span className="text-danger font-ctm--bold">
                {errors["gateway_code"] && errors["gateway_code"]?.["message"]}
              </span>
            </div>

            {!searchGatewaysText &&
            merchantEnabledGateways.length <= 5 ? null : (
              <SearchBar
                name="search-gateways"
                // value={searchGatewaysText}
                placeholder="Search payment providers..."
                onChange={(e: any) => setSearchGatewaysText(e.target.value)}
                onClear={() => setSearchGatewaysText("")}
              />
            )}

            <div className="gateway-links--container">
              {
                // display laoding skeletons while loading merchant gateways
                isLoadingMerchantGateways ? (
                  <GatewaySkeletonLoader message="Loading payment providers for selected country" />
                ) : !merchantEnabledGateways.length ? (
                  <div className="mt-5 mb-5">
                    {searchGatewaysText
                      ? "No results for search..."
                      : "No payment providers available for selected country"}
                  </div>
                ) : (
                  // display bank transfer details if customer selects bank transfer
                  <>
                    <LinkWithIcon
                      key={"monnify"}
                      iconLeft={
                        <img
                          src={bankTransferIcon}
                          className="gateway-logo"
                          alt="bank transfer"
                        />
                      }
                      title={"BANK TRANSFER"}
                      subtitle={`Pay Via Transfer From Your Account`}
                      urlTarget="#"
                      asButton
                      onButtonClick={() => {
                        setGatewayCode("monnify");
                        setSelectedPaymentChannel(
                          switchappPaymentChannel.switchAppVirtualBankTransfer
                        );
                      }}
                      iconRight={
                        selectedGatewayCode === "monnify" &&
                        selectedPaymentChannel ===
                          switchappPaymentChannel.switchAppVirtualBankTransfer ? (
                          <img
                            src={blueCheckMarkIcon}
                            alt="selected icon"
                            className="payment-gateway--selected-icon"
                          />
                        ) : (
                          <div className="radio--unchecked" />
                        )
                      }
                    />

                    {merchantEnabledGateways.map((gwItem) => (
                      <LinkWithIcon
                        key={gwItem.gateway_code}
                        iconLeft={
                          <img
                            src={`/gateway-logos/${gwItem.gateway_code}-icon.svg`}
                            className="gateway-logo"
                            alt="payment provider"
                          />
                        }
                        title={gwItem.gateway_name}
                        subtitle={`Complete Your Payment With ${gwItem.gateway_name}`}
                        urlTarget="#"
                        asButton
                        onButtonClick={() => {
                          setGatewayCode(gwItem.gateway_code);
                          setSelectedPaymentChannel(
                            switchappPaymentChannel.gateway
                          );
                        }}
                        iconRight={
                          selectedGatewayCode === gwItem.gateway_code &&
                          selectedPaymentChannel !==
                            switchappPaymentChannel.switchAppVirtualBankTransfer ? (
                            <img
                              src={blueCheckMarkIcon}
                              alt="selected icon"
                              className="payment-gateway--selected-icon"
                            />
                          ) : (
                            <div className="radio--unchecked" />
                          )
                        }
                      />
                    ))}
                  </>
                )
              }
            </div>

            <div className="flex-container buttons-container mt-5">
              <SubmitButton className=" onboarding-submit--button">
                {hostedPaymentLink.is_top_up_payment_link ? "Top-Up" : "Pay"}
              </SubmitButton>
            </div>
          </>
        )}
      </form>
      <DevTool control={control} /> {/* set up the dev tool */}
    </div>
  );
};
